import { omit } from 'lodash';
import { Overwrite, ValuesType } from 'utility-types';

import { DateRange } from '@app/utils';
import { sortByItemsMessages } from '@app/translations';
import {
  ShapeWithTotalRecords,
  SortDirection,
  SortOption,
} from '@app/constants';

export const facet = {
  status: 'patronItemStatus',
  format: 'sourceTypes',
  location: 'items.effectiveLocationId',
  publisher: 'instancePublishers',
  language: 'languages',
  subject: 'instanceSubjects',
  publication_year: 'publicationYear',
} as const;

export const facetsOrder = Object.values(omit(facet, 'publication_year'));
export type FacetKeys = ValuesType<typeof facetsOrder>;

export enum Operator {
  all = 'all',
  any = 'any',
  equal = '=',
  phraseMatch = '==',
  greaterThan = '>',
  lessThan = '<',
}

export const allRecordsCql = 'cql.allRecords=1';

export const publicationYearFieldsMap = {
  allRecords: allRecordsCql,
  publicationYearMin: `${facet.publication_year}${Operator.greaterThan}`,
  publicationYearMax: `${facet.publication_year}${Operator.lessThan}`,
};

export enum SearchOption {
  keyword = 'keyword',
  title = 'title',
  author = 'author',
  issn = 'issn',
  isbn = 'isbn',
  lccn = 'lccn',
  subject = 'subject',
  advanced = 'advanced',
  query = 'query',
}

export type SearchOptionType = `${SearchOption}`;

export const searchOptionMap = {
  [SearchOption.keyword]: `keyword ${Operator.all}`,
  [SearchOption.title]: `title ${Operator.all}`,
  [SearchOption.author]: `contributors ${Operator.all}`,
  [SearchOption.issn]: `issn ${Operator.equal}`,
  [SearchOption.isbn]: `isbn ${Operator.equal}`,
  [SearchOption.lccn]: `lccn ${Operator.equal}`,
  [SearchOption.subject]: `subjects ${Operator.all}`,
};

export enum SortField {
  relevance = 'relevance',
  title = 'title',
  author = 'author',
  publicationYear = 'publicationYear',
}

export const sortFieldMap = {
  [SortField.title]: 'title',
  [SortField.author]: 'contributors',
  [SortField.publicationYear]: 'publicationYear',
};

export const facetToOperatorMap = {
  [facet.status]: Operator.phraseMatch,
  [facet.location]: Operator.phraseMatch,
  [facet.format]: Operator.phraseMatch,
  [facet.language]: Operator.phraseMatch,
  [facet.publisher]: Operator.phraseMatch,
  [facet.subject]: Operator.phraseMatch,
};

export const identifierType = {
  isbn: 'ISBN',
  issn: 'ISSN',
  lccn: 'LCCN',
  oclc: 'OCLC',
};

export enum ClassificationType {
  lc = 'LC',
  dewey = 'Dewey',
}

export const searchResultSortItems: SortOption[] = [
  {
    label: sortByItemsMessages.relevance,
    sortField: SortField.relevance,
  },
  {
    label: sortByItemsMessages.title,
    sortField: SortField.title,
    sortDirection: SortDirection.asc,
  },
  {
    label: sortByItemsMessages.title,
    sortField: SortField.title,
    sortDirection: SortDirection.desc,
  },
  {
    label: sortByItemsMessages.author,
    sortField: SortField.author,
    sortDirection: SortDirection.asc,
  },
  {
    label: sortByItemsMessages.author,
    sortField: SortField.author,
    sortDirection: SortDirection.desc,
  },
  {
    label: sortByItemsMessages.publicationYear,
    sortField: SortField.publicationYear,
    sortDirection: SortDirection.asc,
  },
  {
    label: sortByItemsMessages.publicationYear,
    sortField: SortField.publicationYear,
    sortDirection: SortDirection.desc,
  },
];

export type SearchResultsDto = ShapeWithTotalRecords<{
  instances?: SearchInstanceDto[];
}>;

export type SearchResultsErrorsDto = ShapeWithTotalRecords<{
  errors: SearchInstanceErrorDto[];
}>;

export interface ItemDto {
  effectiveShelvingOrder: string;
}

export interface ElectronicAccessDto {
  uri?: string;
  linkText?: string;
  materialsSpecification?: string;
  publicNote?: string;
}

export type ElectronicAccessDtoWithURI = Overwrite<
  ElectronicAccessDto,
  { uri: string }
>;

export interface SearchInstanceErrorDto {
  code: string;
  message: string;
  type: string;
}

export interface ParentInstancesDto {
  superInstanceId: string;
}

export interface ChildInstancesDto {
  subInstanceId: string;
}

export type SearchInstanceSubjectsDto =
  | string[]
  | {
      value: string;
    }[];

export interface ContributorDto {
  name: string;
  primary?: boolean;
  contributorTypeName?: string;
  contributorTypeText?: string;
}

export interface PublicationDto {
  dateOfPublication: string;
  place?: string;
  publisher?: string;
}

export type LccnPermalinksSet = {
  marcxml?: string;
  mods?: string;
  bibframe2?: string;
  dc?: string;
};

export type SearchInstanceNoteDto = { labelKey: string; note: string[] };

export interface SearchInstanceDto {
  id: string;
  title: string;
  publication: PublicationDto[];
  electronicAccess: ElectronicAccessDto[];
  parentInstances?: ParentInstancesDto[];
  childInstances?: ChildInstancesDto[];
  source?: string;
  languages?: string[];
  editions?: string[];
  subjects?: SearchInstanceSubjectsDto;
  series?: { value: string }[];
  sourceTypes?: string[];
  contributors?: ContributorDto[];
  alternativeTitles?: { alternativeTitle: string }[];
  precedingTitles?: {
    title: string;
    precedingInstanceId?: string;
  }[];
  succeedingTitles?: {
    title: string;
    succeedingInstanceId?: string;
  }[];
  items?: ItemDto[];
  notes?: SearchInstanceNoteDto[];
  isbns?: string[];
  issns?: string[];
  lccns?: string[];
  oclcs?: string[];
  lccnPermalinks?: LccnPermalinksSet[];
  effectiveShelvingOrder?: string;
  physicalDescriptions?: string[];
  classifications?: {
    classificationNumber: string;
    classificationTypeName: string;
  }[];
}

interface LibraryLocationDto {
  name: string;
  code?: string;
}

export interface HoldingAvailability {
  id: string;
  callNumber: string;
  location: LibraryLocationDto;
  status: HoldingAvailabilityStatus;
  holdingsStatements: { statement: string }[];
  library?: LibraryLocationDto;
  dueDate?: string;
  volume?: string;
}

export interface HoldingsAvailabilityDto {
  instanceId: string;
  requestable: boolean;
  isRTACApplicable: boolean;
  hasItems?: boolean;
  holdings: HoldingAvailability[];
}

export interface SearchState {
  option: SearchOptionType;
  query?: string;
  pageNumber?: number;
  sortField?: SortField;
  sortDirection?: SortDirection;
  facets?: SelectedFacets;
  isInnerUpdate?: boolean;
  publicationYear?: DateRange;
  isNewPageRequest?: boolean;
  recordsPerPage?: number;
  limit?: number;
  offset?: number;
}

export type SelectedFacets = {
  [propName in (typeof facetsOrder)[0]]?: string[];
};

export type SelectedFacetAttributes = Partial<
  Record<FacetKeys, Record<string, boolean>>
>;

export interface HoldingAvailabilityStatus {
  generalStatus: RecordAvailabilityGeneralStatus;
  customIcon: RecordAvailabilityIconColor;
  customDisplayStatus: string;
}

export enum RecordAvailabilityIconColor {
  red = 'red',
  green = 'green',
  yellow = 'yellow',
  error = 'error',
}

export enum RecordAvailabilityGeneralStatus {
  available = 'Available',
  notAvailable = 'Not Available',
  error = 'Error',
  unknown = 'Unknown',
}
